import React, { useEffect, useState } from "react";
import {
  Datagrid,
  DateField,
  List,
  NumberField,
  TextField,
  useListContext,
} from "react-admin";
import {
  Box,
  Button,
  TextField as MuiTextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import CustomPagination from "../../../Components/CustomPagination";

const CustomFilter = () => {
  const { setFilters } = useListContext();

  const [username, setUsername] = useState("");
  const [gameNameFilter, setGameNameFilter] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const oneMonthAgo = new Date();
  oneMonthAgo.setDate(oneMonthAgo.getDate() - 1);
  const defaultStartDate = oneMonthAgo.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    handleApply();
  }, []);

  const handleApply = () => {
    const filters = {};
    if (username.trim()) filters.username = username;
    if (gameNameFilter.trim()) filters.gameName = gameNameFilter;
    if (startDate) filters.startDate = startDate;
    if (endDate) filters.endDate = endDate;
    setFilters(filters, {});
  };

  const handleClear = () => {
    setUsername("");
    setGameNameFilter("");
    setStartDate("");
    setEndDate("");
    setFilters({}, {});
  };

  return (
    <Box display="flex" alignItems="center" gap={2}>
      <MuiTextField
        size="small"
        variant="outlined"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        placeholder="Enter Username"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleApply();
          }
        }}
      />

      <MuiTextField
        size="small"
        variant="outlined"
        value={gameNameFilter}
        onChange={(e) => setGameNameFilter(e.target.value)}
        placeholder="Enter Game Name"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleApply();
          }
        }}
      />

      <MuiTextField
        size="small"
        type="date"
        label="Start Date"
        InputLabelProps={{ shrink: true }}
        value={startDate}
        onChange={(e) => {
          const newStart = e.target.value;
          setStartDate(newStart);
          if (endDate && newStart > endDate) {
            setEndDate("");
          }
        }}
        inputProps={{ max: today }}
      />

      <MuiTextField
        size="small"
        type="date"
        label="End Date"
        InputLabelProps={{ shrink: true }}
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        inputProps={{
          min: startDate || undefined,
          max: today,
        }}
      />

      <Button
        variant="contained"
        sx={{
          background: "#FFC107",
          color: "#0A0A0A",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#FFA000",
          },
        }}
        onClick={handleApply}
      >
        Apply
      </Button>

      <Button
        variant="outlined"
        sx={{
          outline: "1px solid #FFC107",
          color: "white",
          textTransform: "none",
        }}
        onClick={handleClear}
      >
        Clear
      </Button>
    </Box>
  );
};

const PostPagination = () => {
  const { total, page, perPage, setPage, setPerPage } = useListContext();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        width: "100% !important",
        margin: "16px 0px",
      }}
    >
      <CustomPagination
        page={page}
        perPage={perPage}
        total={total}
        setPage={setPage}
        setPerPage={setPerPage}
        componentPage="allgames"
      />
    </Box>
  );
};

export const AllGamesUserData = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        mt: 2,
        backgroundColor: "#242424",
        borderRadius: 2,
        boxShadow: "none !important",
        padding: isSmallScreen ? { xs: 0, sm: 0 } : { xs: 1.5, sm: 2 },
      }}
    >
      <List
        title="All Game Records"
        resource="allGamesUserDataV2"
        filters={<CustomFilter />}
        filter={{}}
        sort={{ field: "createdAt", order: "DESC" }}
        actions={null}
        perPage={10}
        pagination={<PostPagination />}
        sx={{
          "& .RaList-actions": {
            flexWrap: "nowrap",
          },
          "& .RaFilterFormInput-spacer": { display: "none" },
        }}
      >
        <Box style={{ width: "100%", overflowX: "auto" }}>
          <Box
            style={{
              width: "100%",
              position: isSmallScreen ? "absolute" : "relative",
            }}
          >
            <Datagrid
              bulkActionButtons={false}
              sx={{
                overflowX: "auto",
                overflowY: "hidden",
                width: "100%",
                maxHeight: "100%",
                "& .RaDatagrid-table": {
                  width: "100%",
                },
              }}
            >
              <TextField source="username" label="User Name" />
              <TextField source="gameName" label="Game Name" />
              <TextField source="gameType" label="Game Type" />
              <NumberField
                source="beforeBalance"
                label="Opening Balance"
                textAlign="left"
                options={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <NumberField
                source="betAmount"
                label="Bet Amount"
                textAlign="left"
                options={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <NumberField
                source="winloseAmount"
                label="Win Amount"
                textAlign="left"
                options={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <NumberField
                source="afterBalance"
                label="Closing Balance"
                textAlign="left"
                options={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <DateField source="createdAt" label="Date" showTime />
            </Datagrid>
          </Box>
        </Box>
      </List>
    </Box>
  );
};
